import TrackedVideo from "../components/TrackedVideo";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildVideoPathCandidates,
  normalizeBucketFilePath,
  resolveCanonicalClipPath,
  stripExtension
} from "../utils/videoResolver";
import { trackAnalyticsEvent } from "../utils/analytics";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function RunSession() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const trackedSessionRunRef = useRef("");

  const [session, setSession] = useState({ station: [] });
  const [currentStationIndex, setCurrentStationIndex] = useState(0);
  const [videoSrc, setVideoSrc] = useState("");
  const [isResolvingVideo, setIsResolvingVideo] = useState(false);
  const [resolvedCanonicalPath, setResolvedCanonicalPath] = useState("");
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState({ type: "", text: "" });

  const currentStation = session.station?.[currentStationIndex];
  const selectedVideoPath = currentStation?.videoUrl || currentStation?.videoPath || "";
  const videoPathCandidates = useMemo(
    () => buildVideoPathCandidates(selectedVideoPath, resolvedCanonicalPath),
    [resolvedCanonicalPath, selectedVideoPath]
  );

  const fetchSession = useCallback(async () => {
    if (!state?.session) {
      setErrorMessage("Inget pass valdes.");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/training/session-by-id`, {
        headers: {
          token: localStorage.getItem("token")
        },
        params: {
          sessionId: state.session
        }
      });
      setSession(response.data);
      setErrorMessage("");

      if (trackedSessionRunRef.current !== response.data._id) {
        trackedSessionRunRef.current = response.data._id;
        trackAnalyticsEvent("/analytics/track-session-run", {
          sessionId: response.data._id
        });
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte hämta passet.");
    }
  }, [state?.session]);

  async function bucketRequest(queryPath) {
    const normalizedPath = normalizeBucketFilePath(queryPath);
    const response = await axios.get(`${API_URL}/bucket/presigned-url-file`, {
      headers: {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      },
      params: {
        file: encodeURIComponent(normalizedPath)
      }
    });

    return response.data;
  }

  function validateVideoUrl(url) {
    return new Promise((resolve) => {
      const probeVideo = document.createElement("video");

      const cleanup = () => {
        probeVideo.onloadedmetadata = null;
        probeVideo.onerror = null;
        probeVideo.removeAttribute("src");
        probeVideo.load();
      };

      probeVideo.preload = "metadata";
      probeVideo.onloadedmetadata = () => {
        cleanup();
        resolve(true);
      };
      probeVideo.onerror = () => {
        cleanup();
        resolve(false);
      };
      probeVideo.src = url;
    });
  }

  function endSessionRun() {
    navigate("/my-workouts");
  }

  const goToStation = useCallback((index) => {
    const lastIndex = (session.station?.length || 1) - 1;
    setCurrentStationIndex(Math.min(Math.max(index, 0), lastIndex));
  }, [session.station?.length]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    setCurrentStationIndex(0);
  }, [session._id]);

  useEffect(() => {
    function handleKeyboardNavigation(event) {
      const target = event.target;
      if (target instanceof HTMLElement && (
        target.matches("input, textarea, select, button") || target.isContentEditable
      )) {
        return;
      }

      if (event.key === "ArrowLeft" && currentStationIndex > 0) {
        goToStation(currentStationIndex - 1);
      }
      if (event.key === "ArrowRight" && currentStationIndex < (session.station?.length || 0) - 1) {
        goToStation(currentStationIndex + 1);
      }
    }

    window.addEventListener("keydown", handleKeyboardNavigation);
    return () => window.removeEventListener("keydown", handleKeyboardNavigation);
  }, [currentStationIndex, goToStation, session.station?.length]);

  useEffect(() => {
    setNoteDraft(currentStation?.userNote || "");
    setNoteMessage({ type: "", text: "" });
  }, [currentStation?._id, currentStation?.userNote]);

  useEffect(() => {
    setVideoSrc("");
    setIsResolvingVideo(false);
    setResolvedCanonicalPath("");
    setCandidateIndex(0);
  }, [selectedVideoPath, currentStationIndex]);

  useEffect(() => {
    let cancelled = false;

    async function resolveCanonicalPath() {
      if (!currentStation || resolvedCanonicalPath) {
        return;
      }

      const canonicalPath = await resolveCanonicalClipPath({
        selectedVideoPath,
        stationTitle: currentStation.title || ""
      });

      if (!cancelled && canonicalPath) {
        setResolvedCanonicalPath(canonicalPath);
      }
    }

    async function resolveVideoSource() {
      if (!currentStation || videoSrc || videoPathCandidates.length === 0 || !videoPathCandidates[candidateIndex]) {
        return;
      }

      setIsResolvingVideo(true);

      try {
        const candidate = videoPathCandidates[candidateIndex];
        const candidateUrl = await bucketRequest(candidate);
        const isPlayable = await validateVideoUrl(candidateUrl);

        if (cancelled) {
          return;
        }

        if (isPlayable) {
          setVideoSrc(candidateUrl);
          setIsResolvingVideo(false);
          return;
        }

        if (candidateIndex < videoPathCandidates.length - 1) {
          setCandidateIndex((currentIndex) => currentIndex + 1);
          return;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
      }

      if (!cancelled && candidateIndex >= videoPathCandidates.length - 1) {
        setVideoSrc("");
        setIsResolvingVideo(false);
      }
    }

    resolveCanonicalPath();
    resolveVideoSource();

    return () => {
      cancelled = true;
    };
  }, [candidateIndex, currentStation, resolvedCanonicalPath, selectedVideoPath, videoPathCandidates, videoSrc]);

  async function saveStationNote() {
    if (!session._id || !currentStation?._id) {
      return;
    }

    try {
      setIsSavingNote(true);
      const response = await axios.patch(`${API_URL}/training/station-note`, {
        sessionId: session._id,
        stationId: currentStation._id,
        note: noteDraft
      }, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });

      setSession((previousSession) => ({
        ...previousSession,
        station: previousSession.station.map((station) => (
          station._id === currentStation._id
            ? {
              ...station,
              userNote: response.data.note || "",
              userNoteUpdatedAt: response.data.updatedAt || null
            }
            : station
        ))
      }));
      setNoteDraft(response.data.note || "");
      setNoteMessage({
        type: "success",
        text: response.data.note ? "Anteckningen sparades." : "Anteckningen togs bort."
      });
    } catch (error) {
      setNoteMessage({
        type: "error",
        text: error.response?.data?.message || "Kunde inte spara anteckningen."
      });
    } finally {
      setIsSavingNote(false);
    }
  }

  return (
    <div className="flex flex-col items-center px-2 sm:px-4">
      <div className="my-2 w-full max-w-4xl rounded-[22px] bg-gradient-to-br from-slate-50 via-white to-sky-50 p-2 shadow-sm ring-1 ring-slate-200/80 sm:my-4 sm:rounded-[28px] sm:p-4">
        <div className="rounded-[20px] border border-slate-200 bg-white/85 p-3 sm:rounded-[24px] sm:p-4">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Kör pass</p>
          <p className="mt-2 text-center text-lg font-bold text-slate-800">{session.title || "Pass"}</p>
          <p className="mt-2 text-center text-sm text-slate-600">
            {currentStation
              ? `Station ${currentStationIndex + 1} av ${session.station?.length || 0}`
              : "Välj ett pass från Mina träningspass för att starta uppspelningen."}
          </p>
          {currentStation && (
            <div className="mt-3" aria-label={`Förlopp: station ${currentStationIndex + 1} av ${session.station.length}`}>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sky-600 to-cyan-400 transition-[width] duration-300"
                  style={{ width: `${((currentStationIndex + 1) / session.station.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {session.station?.length > 1 && (
          <nav className="mt-3 rounded-[20px] border border-slate-200 bg-white/85 p-2 sm:p-3" aria-label="Välj station">
            <div className="flex snap-x gap-2 overflow-x-auto pb-1">
              {session.station.map((station, index) => (
                <button
                  type="button"
                  key={station._id || index}
                  className={`min-h-[44px] shrink-0 snap-start rounded-2xl border px-4 py-2 text-left text-sm font-semibold transition-colors ${index === currentStationIndex ? "border-sky-500 bg-sky-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-sky-50"}`}
                  onClick={() => goToStation(index)}
                  aria-current={index === currentStationIndex ? "step" : undefined}
                >
                  <span className="block text-xs opacity-80">{index + 1}</span>
                  <span className="block max-w-[11rem] truncate">{stripExtension(station.title) || `Station ${index + 1}`}</span>
                </button>
              ))}
            </div>
          </nav>
        )}

        {errorMessage && (
          <div className="mx-auto mt-4 w-full rounded-2xl border border-red-300 bg-red-50 px-5 py-3 text-center text-sm text-red-800">
            {errorMessage}
          </div>
        )}

        {!errorMessage && session.station?.length === 0 && (
          <div className="mx-auto mt-4 w-full rounded-2xl border border-dashed border-slate-300 bg-white/80 px-5 py-6 text-center text-sm text-slate-500">
            Passet har inga stationer att köra ännu.
          </div>
        )}

        {!errorMessage && currentStation && (
          <div className="mt-3 rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 p-3 shadow-sm sm:mt-4 sm:rounded-[24px] sm:p-4">
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-slate-800">{stripExtension(currentStation.title)}</p>
              <p className="text-sm text-slate-600">{currentStation.description || "Ingen beskrivning"}</p>
            </div>

            <div className="mt-3 overflow-hidden rounded-[18px] bg-slate-950 shadow-sm sm:mt-4 sm:rounded-[24px]">
              {isResolvingVideo && !videoSrc && (
                <div className="flex min-h-[240px] items-center justify-center px-6 py-16 text-sm text-slate-200">
                  Laddar videoklipp...
                </div>
              )}
              {!isResolvingVideo && !videoSrc && (
                <div className="flex min-h-[240px] items-center justify-center px-6 py-16 text-sm text-slate-200">
                  Kunde inte ladda något videoklipp för stationen.
                </div>
              )}
              {videoSrc && (
                <TrackedVideo
                  clipPath={resolvedCanonicalPath || videoPathCandidates[candidateIndex]}
                  clipTitle={stripExtension(currentStation.title || "")}
                  sessionId={session._id}
                  controls
                  autoPlay
                  key={`${currentStation._id}-${candidateIndex}`}
                  className="max-h-[62vh] w-full bg-black object-contain sm:max-h-[70vh]"
                >
                  <source src={videoSrc} />
                </TrackedVideo>
              )}
            </div>

            <div className="mt-3 rounded-[18px] border border-slate-200 bg-white/85 p-3 sm:mt-4 sm:rounded-[20px] sm:p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Mina anteckningar</p>
                  <p className="mt-1 text-sm text-slate-500">Sparas bara för dig i det här passet.</p>
                </div>
                <button
                  className="rounded-[18px] border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={saveStationNote}
                  disabled={isSavingNote || noteDraft === (currentStation.userNote || "")}
                >
                  {isSavingNote ? "Sparar..." : "Spara anteckning"}
                </button>
              </div>
              <textarea
                value={noteDraft}
                className="mt-3 min-h-[96px] w-full rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none transition-colors focus:border-sky-400"
                placeholder="Lägg till egen notering här"
                onChange={(event) => setNoteDraft(event.target.value)}
              />
              {currentStation.userNoteUpdatedAt && (
                <p className="mt-2 text-xs text-slate-500">
                  Senast sparad: {new Intl.DateTimeFormat("sv-SE", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit"
                  }).format(new Date(currentStation.userNoteUpdatedAt))}
                </p>
              )}
              {noteMessage.text && (
                <p className={`mt-2 text-sm ${noteMessage.type === "error" ? "text-red-700" : "text-green-700"}`}>
                  {noteMessage.text}
                </p>
              )}
            </div>

            <div className="sticky bottom-20 z-20 mt-4 grid grid-cols-3 gap-2 rounded-[20px] border border-slate-200 bg-white/95 p-2 shadow-lg backdrop-blur-md sm:static sm:flex sm:flex-wrap sm:justify-between sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
              <button
                className="min-h-[48px] rounded-[16px] border border-slate-200 bg-white px-2 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-[20px] sm:px-5"
                onClick={() => goToStation(currentStationIndex - 1)}
                disabled={currentStationIndex === 0}
              >
                <span aria-hidden="true">← </span>Föregående
              </button>
              <button
                className="min-h-[48px] rounded-[16px] border border-red-200 bg-white px-2 py-3 text-sm font-semibold text-red-700 shadow-sm transition-colors hover:bg-red-50 sm:rounded-[20px] sm:px-5"
                onClick={endSessionRun}
              >
                Avsluta
              </button>
              <button
                className="min-h-[48px] rounded-[16px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-2 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-[20px] sm:px-5"
                onClick={() => goToStation(currentStationIndex + 1)}
                disabled={currentStationIndex >= session.station.length - 1}
              >
                Nästa<span aria-hidden="true"> →</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RunSession;
